import React, { useState, useRef } from 'react';

const Sidebar = ({
  user,
  currentView,
  setCurrentView,
  isMobile,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isSidebarPinned,
  setIsSidebarPinned,
  isSidebarHovered,
  setIsSidebarHovered,
}) => {
  const isCollapsed = !isMobile && !isSidebarPinned && !isSidebarHovered;
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  const handleNavScroll = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  const renderSidebarButton = (viewName, label, subtitle, icon, roles = []) => {
    if (roles.length > 0 && !roles.includes(user?.role)) return null;

    const isActive = currentView === viewName;

    return (
      <button
        key={viewName}
        onClick={() => {
          setCurrentView(viewName);
          if (isMobile) setIsMobileMenuOpen(false);
        }}
        title={isCollapsed ? label : ''}
        className={`w-full flex items-center transition-all duration-150 group rounded-xl ${
          isCollapsed ? 'justify-center p-2.5 my-0.5' : 'justify-between px-2.5 py-2 my-0.5'
        } ${
          isActive
            ? 'bg-gradient-to-r from-emerald-50 via-emerald-100/60 to-teal-50 text-emerald-800 font-bold border border-emerald-300/80 shadow-xs'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 font-semibold'
        }`}
      >
        <div className="flex items-center min-w-0">
          <div
            className={`p-1.5 rounded-lg flex-shrink-0 transition-all ${
              isActive
                ? 'bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white shadow-xs'
                : 'bg-slate-100 group-hover:bg-slate-200 text-slate-600 group-hover:text-slate-900'
            } ${isCollapsed ? '' : 'mr-2.5'}`}
          >
            {icon}
          </div>
          <div
            className={`text-left transition-all duration-200 overflow-hidden ${
              isCollapsed ? 'w-0 max-w-0 opacity-0' : 'w-auto max-w-xs opacity-100'
            }`}
          >
            <div className="whitespace-nowrap text-[13px] font-bold leading-tight">{label}</div>
            {subtitle && !isCollapsed && (
              <div className="text-[11px] text-slate-400 font-medium whitespace-nowrap truncate mt-0.5 leading-tight">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {!isCollapsed && isActive && (
          <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex-shrink-0 ml-1.5 shadow-2xs"></span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Sidebar Shell */}
      <div
        onMouseEnter={() => {
          if (!isMobile) setIsSidebarHovered(true);
        }}
        onMouseLeave={() => {
          if (!isMobile) setIsSidebarHovered(false);
        }}
        className={`${
          isMobile
            ? `fixed inset-y-0 left-0 z-50 w-56 transform transition-transform duration-300 ease-in-out ${
                isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
              }`
            : `sticky top-0 z-30 h-screen transition-all duration-300 ease-in-out ${
                isCollapsed ? 'w-16' : 'w-56'
              }`
        } bg-white border-r border-slate-200/80 shadow-xs flex flex-col justify-between`}
      >
        {/* Top Header Logo */}
        <div
          className={`bg-white border-b border-slate-200/80 h-16 flex items-center justify-between flex-shrink-0 transition-all duration-200 ${
            isCollapsed ? 'px-3 justify-center' : 'px-3.5'
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src="/nd.png"
              alt="NEXDESK Logo"
              className="w-9 h-9 rounded-xl object-contain shadow-xs flex-shrink-0 bg-slate-50 border border-slate-100 p-0.5"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/icon-192x192.png';
              }}
            />
            {!isCollapsed && (
              <div className="min-w-0 animate-fadeIn">
                <h1 className="text-sm font-black text-slate-800 tracking-tight leading-none">NEXDESK</h1>
                <p className="text-[10px] font-semibold text-slate-400 mt-1 leading-none">
                  {user?.role === 'superadmin' ? 'Platform Control' : 'IT Workspace'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items List */}
        <nav
          onScroll={handleNavScroll}
          className={`flex-1 sidebar-scroll ${isScrolling ? 'is-scrolling' : ''} px-2 py-3 space-y-4`}
        >
          {/* Main Workspace Section */}
          <div>
            {!isCollapsed && (
              <div className="px-2 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Workspace
              </div>
            )}
            <div className="space-y-0.5">
              {renderSidebarButton(
                'dashboard',
                'Dashboard',
                'Analytics & Metrics',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              )}

              {renderSidebarButton(
                'tickets',
                'Tickets',
                'Support Queue',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              )}

              {renderSidebarButton(
                'closed-tickets',
                'Closed Tickets',
                'Resolution Archive',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}

              {renderSidebarButton(
                'solutions',
                'Solutions Hub',
                'Knowledge Base & FAQ',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}

              {renderSidebarButton(
                'assets',
                'Hardware Assets',
                'Equipment Fleet',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}

              {renderSidebarButton(
                'chat',
                'Team Chat',
                'Direct Messages',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              )}

              {renderSidebarButton(
                'teams',
                'Teams',
                'Workspace Groups',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              )}
            </div>
          </div>

          {/* Analytics & Health Section */}
          <div>
            {!isCollapsed && (
              <div className="px-2 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Intelligence
              </div>
            )}
            <div className="space-y-0.5">
              {renderSidebarButton(
                'analytics',
                'Analytics',
                'Metrics & SLA',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              )}

              {renderSidebarButton(
                'reports',
                'Executive Reports',
                'SLA & Performance',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}

              {renderSidebarButton(
                'infrastructure',
                'IT Infrastructure',
                'Fleet Health & Logs',
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              )}
            </div>
          </div>

          {/* SuperAdmin Platform Section */}
          {user?.role === 'superadmin' && (
            <div>
              {!isCollapsed && (
                <div className="px-2 pb-1.5 text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  Platform Control
                </div>
              )}
              <div className="space-y-0.5">
                {renderSidebarButton(
                  'clients',
                  'Clients Directory',
                  'Tenants & Quotas',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>,
                  ['superadmin']
                )}

                {renderSidebarButton(
                  'subscription-plans',
                  'Plans & Packages',
                  'Tiers, Pricing & Quotas',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>,
                  ['superadmin']
                )}

                {renderSidebarButton(
                  'superadmin-security',
                  'Security & Governance',
                  'Threats, Audit & Impersonate',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>,
                  ['superadmin']
                )}

                {renderSidebarButton(
                  'superadmin-push',
                  'Bulk Push Alerts',
                  'Mass FCM Broadcasts',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>,
                  ['superadmin']
                )}

                {renderSidebarButton(
                  'superadmin-settings',
                  'Platform Settings',
                  '2FA & System Secrets',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>,
                  ['superadmin']
                )}
              </div>
            </div>
          )}

          {/* Admin Management Section */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <div>
              {!isCollapsed && (
                <div className="px-2 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Administration
                </div>
              )}
              <div className="space-y-0.5">
                {renderSidebarButton(
                  'users',
                  'User Management',
                  'Access & Passwords',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>,
                  ['admin', 'superadmin']
                )}

                {renderSidebarButton(
                  'manage-groups',
                  'Manage Groups',
                  'Group Assignment',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>,
                  ['admin', 'superadmin']
                )}

                {(user?.role === 'superadmin' || user?.webhook_enabled === true || (Array.isArray(user?.plan_features) && user.plan_features.some(f => /webhook/i.test(f)))) && renderSidebarButton(
                  'webhooks',
                  'Webhook Integrations',
                  'Custom Pricing & APIs',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>,
                  ['admin', 'superadmin']
                )}

                {renderSidebarButton(
                  'email-settings',
                  'Email & Notifications',
                  'Custom SMTP & Templates',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>,
                  ['admin', 'superadmin']
                )}

                {user?.role === 'admin' && renderSidebarButton(
                  'subscription',
                  'Billing & Subscription',
                  'Cashfree Plans & Quotas',
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>,
                  ['admin']
                )}
              </div>
            </div>
          )}
        </nav>

        {/* Footer Collapse / Pin Toggle Button */}
        <div className="p-2.5 bg-white border-t border-slate-200/80 flex flex-col items-center gap-1 flex-shrink-0">
          {!isMobile && (
            <button
              onClick={() => {
                const newValue = !isSidebarPinned;
                setIsSidebarPinned(newValue);
                localStorage.setItem('sidebar_pinned', JSON.stringify(newValue));
              }}
              className="w-full flex items-center justify-center p-2 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/80 rounded-xl border border-slate-200 hover:border-emerald-200 shadow-2xs transition-all group"
              title={isSidebarPinned ? 'Collapse Sidebar' : 'Pin Sidebar'}
            >
              <svg
                className={`w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-600 transition-transform duration-200 ${
                  isSidebarPinned ? 'rotate-45' : 'rotate-0'
                } ${isCollapsed ? '' : 'mr-2'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-1.1 1.1M21 21l-3.5-3.5" />
              </svg>
              {!isCollapsed && <span className="truncate">{isSidebarPinned ? 'Collapse' : 'Pin Sidebar'}</span>}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
